import * as DropdownNavMenu from '@radix-ui/react-dropdown-menu';
import {DropdownMenuContentProps} from '@radix-ui/react-dropdown-menu';
import {SideNavigationMenuItemElementProps} from './SideNavigationMenuItemElementTypes';
import {useSideNavigationCollapseContext} from '../../side-navigation-contexts';
import styles from '../SideNavigationMenuItem.module.scss';
import {IconArrowRight, IconOpenLink} from '../../../icons';
import {SideNavigationToolTip} from '../../side-navigation-collapsed-menu-item-tooltip/SideNavigationToolTip';
import {useSideNavigationMenuItemClickContext} from '../../side-navigation-contexts/SideNavigationMenuItemClickContext';

const getTriggerProperties = (props: SideNavigationMenuItemElementProps) => {
    return {
        'data-is-tertiary-child': props.isTertiaryChild,
        'data-level-greater-than-1': props.level > 1,
        'data-level': props.level,
        tabIndex: 0,
        'aria-label': props.label,
        className: props.baseContainerStyling,
    }
}

const getContentProperties = (props: SideNavigationMenuItemElementProps): DropdownMenuContentProps => {
    if (props.isSubMenuItem) {
        return {
            sideOffset: 6,
            alignOffset: undefined,
            className: styles.dropDownContentContainer
        }
    }

    return {
        sideOffset: 6,
        align: ((): DropdownNavMenu.DropdownMenuContentProps['align'] => {
            if (props.isTertiaryChild) return 'end';
            return 'start';
        })(),
        side: 'right',
        className: styles.dropDownContentContainer
    }
}

const TriggerBodyContent = (props: SideNavigationMenuItemElementProps & { isCollapsed: boolean }) => {
    return (
        <>
            <div className={styles.logoLabelContainer}>
                {
                    props.Icon &&
                    <span className={styles.badgeIconContainer}>
                        <props.Icon className={styles.badgeIcon}/>
                    </span>
                }
                {
                    (!props.isCollapsed || props.level !== 0) &&
                    <span className={styles.pillAndLabelContainer}>
                        {
                            props.internalOnly &&
                            <div aria-label={'Internal Only'} className={styles.pill}>
                                <span className={styles.pillText}>
                                    INT
                                </span>
                            </div>
                        }
                        <span className={styles.label}>{props.label}</span>
                    </span>
                }
            </div>
            {
                (!props.isCollapsed || props.level !== 0) && !props.isTertiaryChild && props.children && props.children.length > 0 &&
                <span className={styles.arrowIconContainer}>
                    <IconArrowRight className={styles.menuItemChildrenIcon}/>
                </span>
            }
            {props.externalRoute && !props.route &&
                <IconOpenLink/>
            }
        </>
    );
}

const ContentBodyContent = (props: SideNavigationMenuItemElementProps & { isCollapsed: boolean }) => {
    const menuItemClickContext = useSideNavigationMenuItemClickContext();

    if (props.isSubMenuItem) {
        return (
            props.children && props.children.length > 0 && props.children.map((child, index) => (
                <DropdownNavMenu.Item
                    className={styles.dropDownItem}
                    key={index} onSelect={() => menuItemClickContext.onMenuItemClick(child)}>
                    <SideNavigationMenuItemDropdown
                        {...child}
                        isSubMenuItem={true}
                        baseContainerStyling={props.baseContainerStyling}
                        isTertiaryChild={props.isTertiaryChild}
                    />
                </DropdownNavMenu.Item>
            ))
        );
    }

    return (
        <>
            {
                props.isCollapsed && props.level === 0 &&
                <div className={styles.dropDownHeading}>
                    <span className={styles.dropDownHeadingText}>{props.label}</span>
                </div>
            }
            {
                props.children && props.children.length > 0 && props.children.map((child, index) => (
                    child.children && child.children.length > 0 ? (
                        <SideNavigationMenuItemDropdown
                            {...child}
                            key={index}
                            isSubMenuItem={true}
                            baseContainerStyling={props.baseContainerStyling}
                            isTertiaryChild={props.isTertiaryChild}
                        />
                    ) : (
                        child.isTitle ?
                            <DropdownNavMenu.Label key={index} className={styles.titleContainer}>
                                <span className={styles.titleText}>{child.label}</span>
                            </DropdownNavMenu.Label>
                            :
                            <DropdownNavMenu.Item
                                className={styles.dropDownItem}
                                key={index}
                                onSelect={() => menuItemClickContext.onMenuItemClick(child)}
                            >
                                <SideNavigationMenuItemDropdown
                                    {...child}
                                    isSubMenuItem={true}
                                    baseContainerStyling={props.baseContainerStyling}
                                    isTertiaryChild={props.isTertiaryChild}
                                />
                            </DropdownNavMenu.Item>
                    )
                ))
            }
        </>
    );
}

export const SideNavigationMenuItemDropdown = (props: SideNavigationMenuItemElementProps) => {
    const {isCollapsed} = useSideNavigationCollapseContext();

    if (!props.children || (props.children && props.children.length < 1)) {

        return <NoChildrenMenuItem {...props} isCollapsed={isCollapsed}/>
    }

    if (!props.isSubMenuItem) {
        return <BaseLevelDropdown {...props} isCollapsed={isCollapsed}/>
    }

    if (props.isSubMenuItem) {
        return <SubMenuDropdown {...props} isCollapsed={isCollapsed}/>
    }
}

const BaseLevelDropdown = (props: SideNavigationMenuItemElementProps & { isCollapsed: boolean }) => {
    const triggerProperties = getTriggerProperties(props);
    const contentProperties = getContentProperties(props);

    return (
        <DropdownNavMenu.Root>
            <SideNavigationToolTip text={props.label} condition={props.level === 0 && props.isCollapsed}>
                <DropdownNavMenu.Trigger {...triggerProperties}>
                    <TriggerBodyContent {...props}/>
                </DropdownNavMenu.Trigger>
            </SideNavigationToolTip>
            <DropdownNavMenu.Portal>
                <DropdownNavMenu.Content {...contentProperties}>
                    <ContentBodyContent {...props} />
                    <DropdownNavMenu.Arrow className={styles.sideNavigationDropdownArrow}/>
                </DropdownNavMenu.Content>
            </DropdownNavMenu.Portal>
        </DropdownNavMenu.Root>
    );
}

const SubMenuDropdown = (props: SideNavigationMenuItemElementProps & { isCollapsed: boolean }) => {
    const triggerProperties = getTriggerProperties(props);
    const contentProperties = getContentProperties(props);

    return (
        <DropdownNavMenu.Sub>
            <DropdownNavMenu.SubTrigger {...triggerProperties}>
                <TriggerBodyContent {...props} />
            </DropdownNavMenu.SubTrigger>
            <DropdownNavMenu.Portal>
                <DropdownNavMenu.SubContent {...contentProperties}>
                    <ContentBodyContent {...props} />
                    <DropdownNavMenu.Arrow className={styles.sideNavigationDropdownArrow}/>
                </DropdownNavMenu.SubContent>
            </DropdownNavMenu.Portal>
        </DropdownNavMenu.Sub>
    );
}

const NoChildrenMenuItem = (props: SideNavigationMenuItemElementProps & { isCollapsed: boolean }) => {
    const triggerProperties = getTriggerProperties(props);
    const menuItemClickContext = useSideNavigationMenuItemClickContext();

    return (
        <SideNavigationToolTip text={props.label} condition={props.level === 0 && props.isCollapsed}>
            <button {...triggerProperties}
                    onClick={props.isSubMenuItem ? undefined : () => menuItemClickContext.onMenuItemClick(props)}>
                <TriggerBodyContent  {...props} />
            </button>
        </SideNavigationToolTip>
    );
}
